import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Divider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloseButton from '@monorepo/shared/src/components/common/Buttons/CloseButton';
import HeaderPageViewControls from '@monorepo/shared/src/components/modules/Paging/components/HeaderPageViewControls';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import usePage from '@monorepo/shared/src/hooks/use-page';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import ReadStory from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@legacy/ReadStory';
import ReadAloudModal from 'components/common/Modals/ReadAloudModal';
import WithQuery from '@monorepo/shared/src/components/common/WithQuery';
import NoContent from '@monorepo/shared/src/components/common/NoContent';
import useReadStoryStore from '@monorepo/shared/src/graph/state/query-hooks/use-read-story-store';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';
import {
  ApplicationSection,
  BookType,
  PageLayout,
} from '@monorepo/shared/src/graph/schema-generated-types-educator';
import BookNavigationControl from '@monorepo/shared/src/components/modules/Books/BookNavigationControl';
import theme from '@monorepo/shared/src/themes/defaultTheme';
import ZoomWrapper from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Zoom/components/ZoomWrapper';
import ZoomProvider from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Zoom/providers/ZoomProvider';
import ZoomOverlay from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Zoom/components/ZoomOverlay';
import { formatCharacter, formatPageNumber } from '@monorepo/shared/src/util/telemetry-util';
import { ActivityTypes, TelemetryEvents } from '@monorepo/shared/src/types';
import { AppRoutes } from 'types';
import LetterBookFooter from './LetterBookFooter';

const t = (key, options?) => translate('letterBooks', key, options);

type Props = {
  letter?: string;
};

const baseHeightAndWidth = {
  height: 'calc(100vh - 60px)',
  maxHeight: 'calc(100vh - 60px)',
  maxWidth: '100vw',
  width: '100vw',
  display: 'unset',
};
const EndAdornment = ({ closeModal }) => (
  <Box display="flex" justifyContent="center" height="100%" alignSelf="center" marginRight="-24px">
    <HeaderPageViewControls />
    <Divider orientation="vertical" />
    <CloseButton onClick={closeModal} color={'white'} margin={'unset'} />
  </Box>
);
const LetterBookContent: React.FC<Props> = ({ letter }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [, { openModal, closeModal }] = useModal();
  const [{ noOfPages, zoomed }] = usePage();
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.READ_BOOK);

  const singlePage = noOfPages === 1;

  const marginY = zoomed ? 0 : 30;
  const heightOffset =
    2 * theme.constants.app.menuOffset + theme.constants.app.footerOffset + marginY * 2;

  const { pathname, search } = location;

  const isMainPage = !pathname.includes(`/${AppRoutes.LETTER_TEACHING}`);

  const [{ book, pages, loading, error }] = useReadStoryStore({
    bookType: pathname.includes(AppRoutes.CLASSROOM_BOOKS)
      ? BookType.CLASSROOM_BOOK
      : BookType.LETTER_BOOK,
    letter,
  });

  const character = book?.characters?.length <= 2 ? { ...book?.characters?.[0] } : null;

  const telemetryData = {
    book,
    bookType: book?.type,
    ...(character && { character }),
    isDoublePage: !singlePage,
    isMainPage,
    location,
    pages,
  };

  const createTelemetryEventFn = (type: string, eventData: any) => {
    const isLetterBooks = window.location.pathname.includes('letter-books');
    const readBookTelemData = {
      ...eventData,
      activityType:
        eventData?.activityType === 'readStory'
          ? ActivityTypes.READ_STORY
          : ActivityTypes.READ_ALOUD,
      pageLayout: singlePage ? PageLayout.SINGLE_PAGE : PageLayout.DOUBLE_PAGE,
      section: isLetterBooks ? ApplicationSection.LETTER_BOOKS : ApplicationSection.LETTER_LEARNING,
      character: eventData?.character ? formatCharacter(eventData.character) : undefined,
      pageNumber: formatPageNumber(eventData?.pageNumber),
    };

    fireEvent(readBookTelemData);
  };

  const bookData = {
    book,
    pages,
    loading,
    error,
    createTelemetryEventFn: createTelemetryEventFn,
  };

  useEffect(() => {
    const isActivitySelectionOpen = search.includes(AppRoutes.ACTIVITY_SELECTION);
    isActivitySelectionOpen && handlePlayFindItClick(letter);
  }, [search]);

  const handleReadAloudClick = () => {
    openModal({
      Title: () => (
        <BaseModalTitle
          text={t('readAloud')}
          endAdornment={<EndAdornment closeModal={closeModal} />}
          hideCloseButton={true}
          className={classes.fullScreenModalTitle}
        />
      ),
      body: <ReadAloudModal letter={letter} />,
      options: { fullScreen: true, closeOnBackdropClick: true },
      getContainerStyles: (defaultStyles) => ({
        ...defaultStyles,
        ...baseHeightAndWidth,
        overflow: 'hidden',
      }),
      getPaperStyles: (defaultStyles) => ({
        ...defaultStyles,
        maxWidth: '100vw',
        width: '100vw',
        height: '100%',
      }),
    });
  };

  const handlePlayFindItClick = (letter) => {
    navigate(`/${AppRoutes.ACTIVITY_SELECTION}/${letter}`);
  };

  return (
    <WithQuery loading={loading} error={error} ErrorComponent={NoContent}>
      <ZoomProvider>
        <BookNavigationControl telemetryData={telemetryData} />
        <Box
          style={{
            margin: `${theme.typography.pxToRem(marginY)} 0`,
            height: `calc(100vh - ${theme.typography.pxToRem(heightOffset)})`,
          }}
        >
          <ZoomWrapper>
            <ReadStory singlePage={singlePage} bookData={bookData} isOpen={false} />
            <ZoomOverlay />
          </ZoomWrapper>
        </Box>

        <LetterBookFooter
          onPlayFindItClick={handlePlayFindItClick}
          onReadAloudClick={handleReadAloudClick}
          letter={letter}
        />
      </ZoomProvider>
    </WithQuery>
  );
};

const useStyles = makeStyles(() => ({
  fullScreenModalTitle: {
    alignItems: 'center',
    zIndex: 1,
    height: '60px',
    maxHeight: '60px',
    boxShadow:
      '0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)',
    '& h2': {
      fontWeight: 'bold',
    },
  },
}));

export default LetterBookContent;
